import React from "react";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  Eyebrow,
  DisplayHeading,
  BodyCopy,
  Reveal,
  Badge,
  TextLink,
} from "@/components/kit";
import { PageHero, CTASection } from "@/components/shared";
import { color } from "@/lib/theme";
import { fadeUp, stagger, viewport } from "@/lib/motion";

/* ------------------------------------------------------------------ */
/*  Capability landscape — four connected lanes instead of a table    */
/* ------------------------------------------------------------------ */

const LANES = [
  { key: "trad", name: "Traditional governance" },
  { key: "comp", name: "General compliance" },
  { key: "run", name: "Runtime AI security" },
  { key: "meira", name: "Meira", primary: true },
];

const CAPABILITIES = [
  { cap: "AI security testing", trad: false, comp: false, run: true, meira: true },
  { cap: "AI governance", trad: true, comp: true, run: false, meira: true },
  { cap: "ISO 42001 readiness", trad: false, comp: true, run: false, meira: true },
  { cap: "Multi-framework screening", trad: false, comp: true, run: false, meira: true },
  { cap: "Agentic AI governance", trad: false, comp: false, run: false, meira: true },
  { cap: "Verifiable certificates", trad: false, comp: false, run: false, meira: true },
  { cap: "Trust Passport & Graph", trad: false, comp: false, run: false, meira: true },
  { cap: "Explainable, evidence-led", trad: false, comp: false, run: false, meira: true },
];

function LaneCell({ covered, prev, next, primary }) {
  const line = primary ? "brand" : "brand";
  const seg = primary ? 0.9 : 0.45;
  return (
    <div className={`relative flex h-14 items-center justify-center sm:h-16 ${primary ? "bg-brand/[0.04]" : ""}`}>
      {prev && covered && (
        <span className="absolute left-1/2 top-0 h-1/2 w-px -translate-x-1/2" style={{ background: color(line), opacity: seg }} />
      )}
      {covered && next && (
        <span className="absolute bottom-0 left-1/2 h-1/2 w-px -translate-x-1/2" style={{ background: color(line), opacity: seg }} />
      )}
      {covered ? (
        <span
          className="relative z-10 h-2.5 w-2.5 rounded-full"
          style={{ background: color(line), boxShadow: `0 0 ${primary ? 10 : 5}px ${color(line, primary ? 0.45 : 0.3)}` }}
        />
      ) : (
        <span className="relative z-10 h-px w-3 bg-line" />
      )}
    </div>
  );
}

function CapabilityLanes() {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2 lg:hidden">
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">Swipe to compare</span>
        <span className="h-px flex-1 bg-line-soft" />
        <span className="font-mono text-[11px] text-content-faint" aria-hidden>→</span>
      </div>
      <div className="relative">
        <div className="overflow-x-auto overscroll-x-contain pb-1" data-testid="capability-lanes">
          <div className="grid min-w-[680px] grid-cols-[1.4fr_repeat(4,1fr)]">
        {/* header */}
        <div className="border-b border-line pb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Capability</span>
        </div>
        {LANES.map((l) => (
          <div key={l.key} className={`flex flex-col items-center gap-2 border-b border-line px-2 pb-4 ${l.primary ? "bg-brand/[0.04]" : ""}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${l.primary ? "bg-brand" : "bg-brand-soft/50"}`} />
            <span className={`text-center font-mono text-[10px] uppercase leading-tight tracking-[0.14em] ${l.primary ? "text-brand" : "text-content-faint"}`}>
              {l.name}
            </span>
          </div>
        ))}

        {/* rows */}
        {CAPABILITIES.map((row, r) => (
          <React.Fragment key={row.cap}>
            <div className="flex items-center border-b border-line-soft pr-4 text-[13px] text-content sm:text-sm">
              {row.cap}
            </div>
            {LANES.map((l) => (
              <div key={l.key} className="border-b border-line-soft">
                <LaneCell
                  covered={row[l.key]}
                  prev={r > 0 && CAPABILITIES[r - 1][l.key]}
                  next={r < CAPABILITIES.length - 1 && CAPABILITIES[r + 1][l.key]}
                  primary={l.primary}
                />
              </div>
            ))}
          </React.Fragment>
        ))}
          </div>
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-ink to-transparent lg:hidden" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Test → Govern → Prove — one connected evidence flow               */
/* ------------------------------------------------------------------ */

const ENVIRONMENTS = [
  {
    tag: "01 · Test",
    title: "Controlled test environment",
    accent: "brand",
    body: "Real AI security and red-team assessments run against your systems — producing dated findings, not assumptions.",
    chips: ["Red-team assessment", "Findings", "Severity"],
  },
  {
    tag: "02 · Govern",
    title: "Governance layer",
    accent: "brand-deep",
    body: "Findings become risks — mapped to controls, owners and recognized frameworks like ISO/IEC 42001.",
    chips: ["Risks", "Controls", "Owners", "Frameworks"],
  },
  {
    tag: "03 · Prove",
    title: "Proof layer",
    accent: "brand",
    body: "Controls and evidence roll up into an independently verifiable certificate anyone can check — no login required.",
    chips: ["Evidence", "Trust Passport", "Verifiable certificate"],
  },
];

function FlowConnector() {
  return (
    <div className="flex items-center justify-center" aria-hidden>
      <span className="my-1 h-7 w-px bg-brand/50 lg:my-0 lg:h-px lg:w-10" />
    </div>
  );
}

function EnvironmentFlow() {
  return (
    <div>
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
        className="flex flex-col lg:flex-row lg:items-stretch"
        data-testid="environment-flow"
      >
        {ENVIRONMENTS.map((e, i) => (
          <React.Fragment key={e.tag}>
            <motion.div
              variants={fadeUp}
              className="relative flex-1 overflow-hidden rounded-lg border border-line bg-panel-raised p-5 sm:p-6"
            >
              <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: color(e.accent) }} />
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: color(e.accent), boxShadow: `0 0 8px ${color(e.accent, 0.35)}` }} />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">{e.tag}</span>
              </div>
              <h4 className="mt-3 font-display text-xl font-medium text-content">{e.title}</h4>
              <p className="mt-3 text-[13px] leading-relaxed text-content-muted">{e.body}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {e.chips.map((c) => (
                  <span key={c} className="rounded-full border border-line bg-panel-raised px-2.5 py-1 font-mono text-[10px] tracking-wide text-content-muted">
                    {c}
                  </span>
                ))}
              </div>
            </motion.div>
            {i < ENVIRONMENTS.length - 1 && <FlowConnector />}
          </React.Fragment>
        ))}
      </motion.div>

      <Reveal i={1}>
        <div className="mt-4 flex flex-col gap-2 rounded-lg border border-line bg-brand/[0.05] px-4 py-4 sm:mt-5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 sm:px-5">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand">One evidence layer</span>
          <span className="text-[13px] text-content-muted">
            Testing, governance and proof write to the same connected base — every certificate traces back to a real test.
          </span>
        </div>
      </Reveal>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Claims vs Proof — the core differentiation                         */
/* ------------------------------------------------------------------ */

const CLAIMS = ["Write a policy", "Document controls", "Say it’s governed"];
const PROOF = ["Test the system", "Govern the risk", "Record the evidence", "Issue a certificate"];

function ProofColumn({ variant }) {
  const proof = variant === "proof";
  const steps = proof ? PROOF : CLAIMS;
  const accent = proof ? "valid" : "content-faint";
  return (
    <div
      className={`relative overflow-hidden rounded-lg border p-5 sm:p-7 ${proof ? "border-valid/25 bg-gradient-to-b from-valid/[0.05] to-transparent" : "border-line bg-panel-raised/50"}`}
      data-testid={`proof-column-${variant}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">
        {proof ? "The Meira way" : "The usual approach"}
      </div>
      <h4 className={`mt-2 font-display text-2xl font-medium ${proof ? "text-content" : "text-content-muted"}`}>
        {proof ? "Proof anyone can check" : "Claims you’re asked to trust"}
      </h4>

      <ol className="relative mt-7 flex flex-col">
        <span
          className="absolute left-[7px] top-2 h-[calc(100%-2.75rem)] w-px"
          style={{ background: color(accent), opacity: proof ? 0.6 : 0.25, ...(proof ? {} : { backgroundImage: "none" }) }}
        />
        {steps.map((s, i) => (
          <li key={s} className="relative flex items-center gap-4 pb-6">
            <span
              className="relative z-10 h-3.5 w-3.5 shrink-0 rounded-full border"
              style={{
                borderColor: color(accent, 0.55),
                background: proof ? color(accent) : "transparent",
                boxShadow: proof ? `0 0 8px ${color(accent, 0.4)}` : "none",
              }}
            />
            <span className={`text-sm ${proof ? "text-content" : "text-content-muted"}`}>{s}</span>
          </li>
        ))}
        <li className="relative flex items-center gap-4">
          <span
            className="relative z-10 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full"
            style={{ background: proof ? color("valid") : "transparent", border: proof ? "none" : `1px dashed ${color("line-bright")}` }}
          >
            {proof && <span className="text-[8px] font-bold text-ink">✓</span>}
          </span>
          <span className={`font-mono text-[11px] uppercase tracking-[0.16em] ${proof ? "text-valid" : "text-content-faint"}`}>
            {proof ? "Independently verified" : "…just trust us"}
          </span>
        </li>
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Seven principles — connected spine                                 */
/* ------------------------------------------------------------------ */

const PRINCIPLES = [
  ["01", "Proof over claims", "We don’t just tell you your AI is governed — we generate evidence and verifiable proof."],
  ["02", "Test, don’t just document", "Real security testing sits at the core, not paperwork after the fact."],
  ["03", "One evidence layer", "Testing, governance and proof write to a single, connected evidence base."],
  ["04", "Explainable by design", "The Trust Graph makes every risk, control and outcome traceable."],
  ["05", "Built for agentic AI", "Governance that understands what an agent can access and do."],
  ["06", "Regionally grounded", "Built in the UAE, aligned to global standards and regional rules."],
  ["07", "Accessible beyond Fortune 500", "Enterprise-grade assurance that mid-market teams can actually adopt."],
];

function PrincipleSpine() {
  return (
    <motion.ol
      variants={stagger}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      className="relative mt-10 sm:mt-12"
      data-testid="principle-spine"
    >
      <span className="absolute left-[15px] top-3 h-[calc(100%-3rem)] w-px bg-gradient-to-b from-brand/60 via-line to-brand/60" />
      {PRINCIPLES.map(([n, t, d]) => (
        <motion.li key={n} variants={fadeUp} className="relative flex gap-4 pb-8 last:pb-0 sm:gap-6 sm:pb-10">
          <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line bg-ink">
            <span className="font-mono text-[11px] text-brand">{n}</span>
          </span>
          <div className="pt-0.5">
            <h3 className="font-display text-xl font-medium text-content">{t}</h3>
            <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-content-muted">{d}</p>
          </div>
        </motion.li>
      ))}
      <motion.li variants={fadeUp} className="relative flex items-center gap-4 sm:gap-6">
        <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-brand/40 bg-ink">
          <span className="h-2 w-2 rounded-full bg-brand" style={{ boxShadow: `0 0 8px ${color("brand", 0.4)}` }} />
        </span>
        <Badge tone="brand">Built in the UAE · Designed for global AI trust</Badge>
      </motion.li>
    </motion.ol>
  );
}

/* ------------------------------------------------------------------ */

export default function WhyMeira() {
  return (
    <>
      <PageHero
        eyebrow="Why Meira"
        titleLines={["AI governance is crowded.", "Verifiable assurance isn’t."]}
        intro="Few, if any, platforms combine AI security testing, governance, global standards and independently verifiable proof in one integrated experience."
      />

      {/* capability landscape */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal>
            <Eyebrow>By capability, not by name</Eyebrow>
          </Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">
            Where the categories stop, Meira continues.
          </DisplayHeading>
          <Reveal i={1}>
            <BodyCopy className="mt-5 max-w-2xl">
              Each category covers a slice. Follow the lanes — Meira’s stays unbroken from testing all the way to verifiable proof.
            </BodyCopy>
          </Reveal>
          <Reveal i={2}>
            <div className="mt-8 sm:mt-12">
              <CapabilityLanes />
            </div>
          </Reveal>
          <p className="mt-6 font-mono text-[11px] tracking-wide text-content-faint">
            Illustrative category comparison. Individual products vary.
          </p>
        </Container>
      </Section>

      {/* connected environments */}
      <Section className="border-t border-line">
        <Container>
          <Reveal>
            <Eyebrow tone="brand">One integrated flow</Eyebrow>
          </Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">
            Test. Govern. Prove — without the seams.
          </DisplayHeading>
          <Reveal i={1}>
            <BodyCopy className="mt-5 max-w-2xl">
              Most teams stitch these together across separate tools. Meira runs them as one connected pipeline, so nothing is lost between test, control and certificate.
            </BodyCopy>
          </Reveal>
          <Reveal i={2}>
            <div className="mt-8 sm:mt-12">
              <EnvironmentFlow />
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* claims vs proof */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal>
            <Eyebrow tone="valid">Claims vs proof</Eyebrow>
          </Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">
            Anyone can say their AI is governed.
          </DisplayHeading>
          <Reveal i={1}>
            <BodyCopy className="mt-5 max-w-2xl">
              The difference is whether an outsider can check it. Meira ends on a verifiable record — not a promise.
            </BodyCopy>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:mt-12 sm:gap-6 lg:grid-cols-2">
            <Reveal i={1}>
              <ProofColumn variant="claims" />
            </Reveal>
            <Reveal i={2}>
              <ProofColumn variant="proof" />
            </Reveal>
          </div>
          <Reveal i={3}>
            <div className="mt-8">
              <TextLink to="/assurance">See how the Trust Graph connects every proof</TextLink>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* principles */}
      <Section className="border-t border-line">
        <Container>
          <Reveal>
            <Eyebrow tone="brand">Principles</Eyebrow>
          </Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">
            Seven principles behind everything we build.
          </DisplayHeading>
          <PrincipleSpine />
        </Container>
      </Section>

      <CTASection eyebrow="See the difference" title={["Governance monitors.", "Meira proves."]} />
    </>
  );
}
